import Logger from "@helpers/Logger";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";

type UploadFunction = (image: File) => Promise<void>;

export const MAX_CONCURRENT_UPLOADS_DEFAULT = 5;
export const MAX_CONCURRENT_VIDEO_UPLOADS_DEFAULT = 2;

export type ParallelAssetUploaderOptions = {
  maxConcurrentVideoUploads?: number;
  isVideo?: (file: File) => boolean;
};

// Uploads images and videos in parallel, with global and video-specific concurrency caps.
export class ParallelAssetUploader {
  async uploadImagesAndVideos(
    images: File[],
    uploadFunc: UploadFunction,
    maxConcurrentUploads = MAX_CONCURRENT_UPLOADS_DEFAULT,
    progressTracker: (isUploadInProgress: boolean, progress: number) => void,
    options?: ParallelAssetUploaderOptions
  ): Promise<void> {
    const maxConcurrentVideoUploads = options?.maxConcurrentVideoUploads ?? MAX_CONCURRENT_VIDEO_UPLOADS_DEFAULT;
    const isVideo = options?.isVideo ?? (() => false);

    let activeUploads = 0;
    let activeVideoUploads = 0;
    const started = images.map(() => false);
    const whichImagesIndexesUploaded = images.map(() => false);

    progressTracker(true, 0);

    Logger.info(`Uploading ${images.length} assets. MaxConcurrent: ${maxConcurrentUploads}, maxVideos: ${maxConcurrentVideoUploads}`);
    // Per-file errors are the caller's responsibility; this class only orchestrates concurrency.
    return new Promise(resolve => {
      const attemptUpload = async () => {
        if (started.every(s => s) && activeUploads === 0) {
          Logger.info(`All ${images.length} assets ran.`);
          resolve();
          progressTracker(false, 100);
          return;
        }

        function setUploadProgress(currentUploadIndex: number) {
          whichImagesIndexesUploaded[currentUploadIndex] = true;
          progressTracker(true, (whichImagesIndexesUploaded.filter(i => i).length / images.length) * 100);

          if (whichImagesIndexesUploaded.filter(i => i).length === images.length) {
            progressTracker(false, 100);
          }
        }

        // Scan ahead: may start a later image while waiting for a video slot.
        while (activeUploads < maxConcurrentUploads) {
          const nextIndex = findNextStartableIndex();
          if (nextIndex === null) {
            // Nothing startable right now; in-flight uploads will call attemptUpload again on finish.
            break;
          }

          const imageName = images[nextIndex]?.name;
          const assetSize = convertToKBorMB(images[nextIndex]?.size);
          Logger.info(`Starting upload for asset '${imageName}' ${assetSize} (${nextIndex + 1} of ${images.length})`);

          // Guard against double-start if attemptUpload is re-entered while promises are in flight.
          started[nextIndex] = true;
          activeUploads++;
          const fileIsVideo = isVideo(images[nextIndex]);
          if (fileIsVideo) {
            activeVideoUploads++;
          }

          uploadFunc(images[nextIndex])
            .then(() => {
              Logger.info(`ParallelUpload ran for asset '${imageName}' ${assetSize} (${nextIndex + 1} of ${images.length})`);
              setUploadProgress(nextIndex);
            })
            .catch(error => {
              const remoteLogger = getGlobalRemoteLogger();
              remoteLogger.error(`Asset UploadError: ${error.message} assetName='${imageName}' ${assetSize} ${nextIndex + 1} of ${images.length}`);
              setUploadProgress(nextIndex);
            })
            .finally(() => {
              // Always release slots so a blocked video can start when an earlier one finishes.
              activeUploads--;
              if (fileIsVideo) {
                activeVideoUploads--;
              }
              attemptUpload();
            });
        }
      };

      // Lowest not-yet-started index that fits both the global and video-specific caps.
      function findNextStartableIndex(): number | null {
        for (let i = 0; i < images.length; i++) {
          if (started[i]) {
            continue;
          }
          const file = images[i];
          // Skip blocked videos but keep scanning — images further back may still start.
          if (isVideo(file) && activeVideoUploads >= maxConcurrentVideoUploads) {
            continue;
          }
          return i;
        }
        return null;
      }

      attemptUpload();
    });
  }
}

function convertToKBorMB(input: number | null | undefined): string | null {
  if (input === null || input === undefined) {
    return null;
  }

  const bytesInKB = 1024;
  const bytesInMB = 1024 * bytesInKB;

  if (input < bytesInKB) {
    return Math.round(input) + "bytes";
  } else if (input < bytesInMB) {
    return Math.round(input / bytesInKB) + "KB";
  } else {
    return Math.round(input / bytesInMB) + "MB";
  }
}
